import { Box, Loader, Text, TextButton } from '@wix/design-system';
import { Trans, useExperiments, useTranslation } from '@wix/fe-essentials/react';
import { OverviewSection, OverviewSectionSkins, RulesPanelUIProvider, useOnRuleGroupToggle } from '@wix/rules-panel-ui';
import { useAtom, useAtomValue, useStore } from 'jotai';
import React from 'react';
import { isScanningAtom, scrollerNodeRefAtom, unsavedChangedHandlersAtom } from '../atoms/session-atoms';
import { showSaveBeforeCloseModal } from '../components/modals/show-save-before-leave-modal';
import { usePanelStateAtom } from '../hooks/rules-state';
import { useA11yWizardAdapter } from '../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../hooks/use-bi-logger';
import { EditorHost } from '../providers';
import { dataHooks } from '../utils/datahooks';
import { isEditor3Host } from '../utils/get-editor-host';
import { renderDividedRuleLists } from './build-divided-group-component';
import { CardsIssueList } from './cards-issue-list';
import { DividedIssuesList } from './divided-issues-list';
export const A11yWizardDetectedIssuesTab = _ref => {
  let {
    ruleGroups,
    issuesNumber,
    goToManualTasks
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    experiments
  } = useExperiments();
  const useNewRuleGroupList = experiments.enabled('specs.responsive-editor.RefactorGroupList');
  const useNewCardsUI = experiments.enabled('specs.a11y-wizard.useCardsUI');
  const {
    getPagesData
  } = usePanelStateAtom();
  const pagesData = getPagesData();
  const biLogger = useBiLogger();
  const {
    a11yWizardAdapter,
    editorHost
  } = useA11yWizardAdapter();
  const isEditor3 = isEditor3Host(editorHost);
  const store = useStore();
  const [unsavedChangedHandlers, setUnsavedChangedHandlers] = useAtom(unsavedChangedHandlersAtom);
  const unsavedChangedHandlersProps = {
    setUnsavedChanges: (exists, apply) => setUnsavedChangedHandlers({
      apply,
      exists
    }),
    showSaveBeforeLeaveModal: (handleApply, handleDiscard) => showSaveBeforeCloseModal({
      a11yWizardAdapter,
      biLogger,
      editorHost,
      handleApply,
      handleDiscard,
      t
    }),
    unsavedChanges: unsavedChangedHandlers ? unsavedChangedHandlers : {
      apply: () => Promise.resolve(),
      exists: false
    }
  };
  const toggleFunctions = useOnRuleGroupToggle({
    unsavedChangesHandlers: unsavedChangedHandlersProps
  });
  const nodeRef = useAtomValue(scrollerNodeRefAtom);
  const isScanning = useAtomValue(isScanningAtom);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    padding: "18px 20px"
  }, /*#__PURE__*/React.createElement(OverviewSection, {
    dataHook: dataHooks.overviewSectionDetectedIssues,
    description: issuesNumber > 0 || isScanning ? t(isScanning && issuesNumber === 0 ? 'accessibility-wizard.detected-issues.overview-section.loading.description' : 'accessibility-wizard.detected-issues.overview-section.description') : /*#__PURE__*/React.createElement(Text, {
      size: "tiny",
      weight: "thin",
      secondary: true
    }, /*#__PURE__*/React.createElement(Trans, {
      components: {
        Link: /*#__PURE__*/React.createElement(TextButton, {
          size: "tiny",
          skin: "dark",
          underline: "always",
          weight: "thin",
          onClick: goToManualTasks
        })
      },
      i18nKey: "accessibility-wizard.detected-issues.overview-section.go-to-manual",
      t: t
    })),
    detectedIssues: isScanning ? issuesNumber === 0 ? /*#__PURE__*/React.createElement(Loader, {
      size: "tiny"
    }) : issuesNumber : issuesNumber,
    showShadow: !isEditor3,
    skin: OverviewSectionSkins.warning,
    title: t(isScanning && issuesNumber === 0 ? 'accessibility-wizard.detected-issues.overview-section.loading.title' : issuesNumber > 0 ? 'accessibility-wizard.detected-issues.overview-section.title' : 'accessibility-wizard.detected-issues.overview-section.title.no-issues', {
      count: issuesNumber
    })
  })), editorHost === EditorHost.odeditor || useNewCardsUI ? /*#__PURE__*/React.createElement(CardsIssueList, null) : /*#__PURE__*/React.createElement("div", {
    style: {
      overflow: 'visible'
    }
  }, /*#__PURE__*/React.createElement(RulesPanelUIProvider, {
    store: store,
    unsavedChangesHandlers: unsavedChangedHandlersProps,
    onToggleFunctions: toggleFunctions
  }, useNewRuleGroupList ? /*#__PURE__*/React.createElement(DividedIssuesList, {
    nodeRef: nodeRef
  }) : renderDividedRuleLists({
    editorHost,
    nodeRef,
    pagesData,
    ruleGroups
  }))));
};
//# sourceMappingURL=a11y-wizard-detected-issues-tab.js.map